"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import "@/components/ui/whatsapp/styles/whatsapp.css";
import { type MessageStatus } from "./message-status";

export interface ImageItem {
  messageId: string;
  url: string;
}

interface ImageBubbleProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "incoming" | "outgoing";
  /** Single image URL (shorthand) */
  src?: string;
  /** Multiple image URLs (shorthand) */
  images?: string[];
  /** Structured items containing messageId and url (preferred for grid/album) */
  items?: ImageItem[];
  messageId?: string;
  alt?: string;
  caption?: string;
  timestamp?: string;
  status?: MessageStatus;
  showTail?: boolean;
  /** Callback recebe messageId e url original da imagem com erro */
  onImageError?: (messageId: string, url: string) => void;
  onRecover?: (messageId: string, url: string) => Promise<boolean>;
  reloadKeys?: Record<string, number>;
  onImageClick?: (messageId: string, url: string) => void;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 11" height="11" width="16" className={className}>
      <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.011-2.175a.463.463 0 0 0-.349-.158.467.467 0 0 0-.338.131.537.537 0 0 0-.14.353.54.54 0 0 0 .13.363l2.39 2.576a.465.465 0 0 0 .349.158.457.457 0 0 0 .368-.189l6.596-8.14a.504.504 0 0 0 .103-.36.516.516 0 0 0-.223-.271z" fill="currentColor" />
    </svg>
  );
}

function DoubleCheckIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 11" height="11" width="16" className={className}>
      <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.011-2.175a.463.463 0 0 0-.349-.158.467.467 0 0 0-.338.131.537.537 0 0 0-.14.353.54.54 0 0 0 .13.363l2.39 2.576a.465.465 0 0 0 .349.158.457.457 0 0 0 .368-.189l6.596-8.14a.504.504 0 0 0 .103-.36.516.516 0 0 0-.223-.271z" fill="currentColor" />
      <path d="M15.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-1.2-1.298-.728.897 1.58 1.704a.465.465 0 0 0 .349.158.457.457 0 0 0 .368-.189l6.596-8.14a.504.504 0 0 0 .103-.36.516.516 0 0 0-.193-.484z" fill="currentColor" />
    </svg>
  );
}

// ─── Media cell with loading skeleton & error fallback ───────────────────────

function ImageCell({
  item,
  overlay,
  className,
  naturalSize = false,
  onImageError,
  onRecover,
  reloadKey = 0,
  onImageClick,
}: {
  item: ImageItem;
  overlay?: React.ReactNode;
  className?: string;
  naturalSize?: boolean;
  onImageError?: (messageId: string, url: string) => void;
  onRecover?: (messageId: string, url: string) => Promise<boolean>;
  reloadKey?: number;
  onImageClick?: (messageId: string, url: string) => void;
}) {
  const [loaded, setLoaded] = React.useState(false);
  const [hasError, setHasError] = React.useState(!item.url || item.url.trim() === '');
  const [recovering, setRecovering] = React.useState(false);
  const [recoveryFailed, setRecoveryFailed] = React.useState(false);
  const flightRef = React.useRef<object | null>(null);
  const generationRef = React.useRef(0);
  const imageRef = React.useRef<HTMLImageElement | null>(null);

  React.useLayoutEffect(() => {
    generationRef.current++;
    flightRef.current = null;
    setRecovering(false);
    setRecoveryFailed(false);
    return () => { generationRef.current++; flightRef.current = null; };
  }, [item.messageId, item.url, reloadKey]);

  React.useLayoutEffect(() => {
    const empty = !item.url || item.url.trim() === '';
    setHasError(empty);
    // Uma imagem já decodificada em cache não depende de receber um novo evento load.
    setLoaded(!empty && !!imageRef.current?.complete && imageRef.current.naturalWidth > 0);
  }, [item.url, item.messageId, reloadKey]);

  React.useEffect(() => {
    if (!item.url || item.url.trim() === '') return;
    const generation = generationRef.current;
    const timeout = setTimeout(() => {
      if (generationRef.current !== generation) return;
      const image = imageRef.current;
      if (image?.complete && image.naturalWidth > 0) {
        setLoaded(true);
      } else {
        // Expor uma ação explícita, sem disparar recovery POST por simples lentidão.
        setLoaded(true);
        setHasError(true);
      }
    }, 15_000);
    return () => clearTimeout(timeout);
  }, [item.url, item.messageId, reloadKey]);

  const handleImageError = () => {
    setLoaded(true);
    setHasError(true);
    if (item.messageId) {
      onImageError?.(item.messageId, item.url);
    }
  };

  return (
    <div className={cn("relative overflow-hidden bg-wa-gray-800", className)} key={`${item.messageId || item.url}-${reloadKey}`}>
      {!loaded && !hasError && (
        <div className={cn("animate-pulse bg-wa-gray-700/70", naturalSize ? "min-h-[160px] w-full" : "absolute inset-0")} />
      )}

      {hasError ? (
        <div className="flex flex-col items-center justify-center p-4 min-h-[140px] w-full bg-gray-100 dark:bg-[#1f2c34] text-gray-400">
          <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-1 text-gray-400">
            <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
            <circle cx="9" cy="9" r="2"/>
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
            <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
          <span className="text-[11px] font-medium text-gray-500 mb-2">Imagem indisponível</span>
          
          {/* Botão de reconstrução/retry acessível */}
          {item.messageId && onRecover && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                if (flightRef.current || !onRecover) return;
                const flight = {};
                const generation = generationRef.current;
                flightRef.current = flight;
                setRecovering(true);
                setRecoveryFailed(false);
                try {
                  const success = await onRecover(item.messageId, item.url);
                  if (generationRef.current === generation) setRecoveryFailed(!success);
                } catch {
                  if (generationRef.current === generation) setRecoveryFailed(true);
                } finally {
                  if (flightRef.current === flight) {
                    flightRef.current = null;
                    if (generationRef.current === generation) setRecovering(false);
                  }
                }
              }}
              disabled={recovering}
              className="mt-1 px-3 py-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white text-[12px] font-semibold rounded-full shadow transition-colors duration-150 cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-400"
              type="button"
              aria-busy={recovering}
              aria-label={`Recuperar mídia para a mensagem ${item.messageId}`}
            >
              {recovering ? 'Recuperando...' : 'Recuperar mídia'}
            </button>
          )}
          {recoveryFailed && <span role="alert" className="mt-2 text-[11px]">Falha ao recuperar mídia. Tente novamente.</span>}
        </div>
      ) : (
        <img
          key={`${item.url}-${reloadKey}`}
          ref={node => {
            imageRef.current = node;
            if (node?.complete && node.naturalWidth > 0) setLoaded(true);
          }}
          src={item.url}
          alt=""
          role={onImageClick ? 'button' : undefined}
          tabIndex={onImageClick ? 0 : undefined}
          aria-label={onImageClick ? `Abrir imagem da mensagem ${item.messageId}` : undefined}
          onClick={() => { if (loaded) onImageClick?.(item.messageId, item.url); }}
          onKeyDown={event => {
            if (loaded && onImageClick && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              onImageClick(item.messageId, item.url);
            }
          }}
          loading="eager"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={handleImageError}
          className={cn(
            "w-full object-cover transition-opacity duration-200",
            naturalSize ? "block max-h-[360px]" : "h-full",
            loaded ? "opacity-100" : "opacity-0"
          )}
        />
      )}

      {overlay && <div className="absolute inset-0 pointer-events-none">{overlay}</div>}
    </div>
  );
}

// ─── Grid layouts ─────────────────────────────────────────────────────────────

function ImageGrid({
  items,
  onImageError,
  onRecover,
  reloadKeys,
  onImageClick
}: {
  items: ImageItem[];
  onImageError?: (messageId: string, url: string) => void;
  onRecover?: (messageId: string, url: string) => Promise<boolean>;
  reloadKeys?: Record<string, number>;
  onImageClick?: (messageId: string, url: string) => void;
}) {
  const visible = items.slice(0, 4);
  const extra = items.length - 4;
  const count = visible.length;

  if (count === 1) {
    const key = reloadKeys?.[visible[0].messageId] || 0;
    return <ImageCell item={visible[0]} className="w-full max-h-[330px]" naturalSize onImageError={onImageError} onRecover={onRecover} onImageClick={onImageClick} reloadKey={key} />;
  }

  const cellClass = "h-[165px] w-[165px]";

  if (count === 2) {
    return (
      <div className="grid grid-cols-2 gap-[2px]">
        {visible.map((item, index) => {
          const key = reloadKeys?.[item.messageId] || 0;
          return (
            <ImageCell key={`${item.messageId}-${index}-${key}`} item={item} className={cellClass} onImageError={onImageError} onRecover={onRecover} onImageClick={onImageClick} reloadKey={key} />
          );
        })}
      </div>
    );
  }

  if (count === 3) {
    const key0 = reloadKeys?.[visible[0].messageId] || 0;
    const key1 = reloadKeys?.[visible[1].messageId] || 0;
    const key2 = reloadKeys?.[visible[2].messageId] || 0;
    return (
      <div className="grid grid-cols-2 gap-[2px]">
        <ImageCell item={visible[0]} className="row-span-2 h-[332px] w-[165px]" onImageError={onImageError} onRecover={onRecover} onImageClick={onImageClick} reloadKey={key0} />
        <ImageCell item={visible[1]} className={cellClass} onImageError={onImageError} onRecover={onRecover} onImageClick={onImageClick} reloadKey={key1} />
        <ImageCell item={visible[2]} className={cellClass} onImageError={onImageError} onRecover={onRecover} onImageClick={onImageClick} reloadKey={key2} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-[2px]">
      {visible.map((item, i) => {
        const isLast = i === 3 && extra > 0;
        const key = reloadKeys?.[item.messageId] || 0;
        return (
          <ImageCell
            key={`${item.messageId}-${i}-${key}`}
            item={item}
            className={cellClass}
            onImageError={onImageError}
            onRecover={onRecover}
            onImageClick={onImageClick}
            reloadKey={key}
            overlay={
              isLast ? (
                <div className="flex h-full items-center justify-center bg-black/50">
                  <span className="text-[22px] font-semibold text-white">+{extra}</span>
                </div>
              ) : undefined
            }
          />
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const ImageBubble = React.forwardRef<HTMLDivElement, ImageBubbleProps>(
  (
    {
      className,
      variant = "incoming",
      src,
      images,
      items,
      messageId,
      alt = "",
      caption,
      timestamp,
      status,
      showTail = false,
      onImageError,
      onRecover,
      reloadKeys,
      onImageClick,
      ...props
    },
    ref
  ) => {
    const isOutgoing = variant === "outgoing";

    const normalizedItems: ImageItem[] = React.useMemo(() => {
      if (items && items.length > 0) return items;
      if (images && images.length > 0) {
        return images.map((url) => ({
          messageId: messageId || '',
          url
        }));
      }
      if (src !== undefined) {
        return [{ messageId: messageId || '', url: src }];
      }
      return [];
    }, [items, images, src, messageId]);

    const statusIcon = () => {
      if (!isOutgoing || !status) return null;
      if (status === "sending" || status === "sent")
        return <span className="text-white opacity-75"><CheckIcon /></span>;
      if (status === "delivered")
        return <span className="text-white opacity-90"><DoubleCheckIcon /></span>;
      if (status === "read")
        return <span className="text-wa-read"><DoubleCheckIcon /></span>;
      if (status === "imported")
        return (
          <span className="text-white opacity-75" title="Histórico importado — envio não comprovado" aria-label="Histórico importado — envio não comprovado">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <polyline points="12 7 12 12 15.5 14" />
            </svg>
          </span>
        );
    };

    return (
      <div
        key={messageId || src || (normalizedItems[0]?.messageId)}
        className={cn("flex w-full", isOutgoing ? "justify-end" : "justify-start", showTail ? "mb-[6px]" : "", className)}
        {...props}
        ref={ref}
      >
        <div
          className={cn(
            "font-wa relative w-full max-w-[380px] md:max-w-[420px] overflow-hidden rounded-lg",
            showTail && (isOutgoing ? "rounded-br-[3px]" : "rounded-bl-[3px]")
          )}
        >
          {showTail && (
            <svg viewBox="0 0 8 13" width="8" height="13" className={cn("absolute bottom-0 z-10", isOutgoing ? "-right-[8px]" : "-left-[8px]")}>
              {isOutgoing
                ? <path d="M5.188 13H0V1.807l6.467 8.625C7.526 11.844 6.958 13 5.188 13z" className="fill-wa-bubble-outgoing" />
                : <path d="M1.533 10.432L8 1.807V13H2.812C1.042 13 .474 11.844 1.533 10.432z" className="fill-wa-bubble-incoming" />
              }
            </svg>
          )}

          {/* Media / Grid */}
          <div className="relative">
            <ImageGrid items={normalizedItems} onImageError={onImageError} onRecover={onRecover} reloadKeys={reloadKeys} onImageClick={onImageClick} />

            {/* Timestamp overlay (no caption) */}
            {!caption && (
              <div className="absolute bottom-[6px] right-[8px] flex items-center gap-[3px] rounded-full bg-black/40 px-[6px] py-[2px]">
                {timestamp && <span className="text-[11px] leading-[15px] text-white">{timestamp}</span>}
                {statusIcon()}
              </div>
            )}
          </div>

          {/* Caption */}
          {caption && (
            <div className={cn("flow-root px-[12px] pb-2 pt-[6px]", isOutgoing ? "bg-wa-bubble-outgoing" : "bg-wa-bubble-incoming")}>
              <div className="float-right -mb-1 ml-2 mt-1 flex items-center gap-[3px]">
                {timestamp && <span className="text-[11px] leading-[15px] text-wa-bubble-meta">{timestamp}</span>}
                {isOutgoing && status && (
                  status === "read"
                    ? <span className="text-wa-read"><DoubleCheckIcon /></span>
                    : status === "delivered"
                    ? <span className="text-wa-delivered"><DoubleCheckIcon /></span>
                    : <span className="text-wa-delivered"><CheckIcon /></span>
                )}
              </div>
              <p className="text-[15px] leading-[20px] text-wa-text-primary whitespace-pre-wrap break-words [overflow-wrap:anywhere] min-w-0">{caption}</p>
            </div>
          )}
        </div>
      </div>
    );
  }
);
ImageBubble.displayName = "ImageBubble";

export { ImageBubble, type ImageBubbleProps };
